import { useMemo, useState, useCallback } from 'react';
import useEnhancedEffect from '../useEnhancedEffect/useEnhancedEffect';
import { createMediaQueries, getCurrentBreakPoint } from './utils';
import type {
  Breakpoint,
  BaseConfig,
  UseResponsiveResponse,
  EmptyBreakpoint,
  UseResponsiveParams
} from './types';

const useResponsive = <C extends BaseConfig, D extends keyof C | undefined>({
  config,
  defaultBreakpoint
}: UseResponsiveParams<C, D>): UseResponsiveResponse<C, D> => {
  const mediaQueries = useMemo(() => createMediaQueries(config), [config]);

  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint<C> | EmptyBreakpoint>(
    getCurrentBreakPoint<C, D>(mediaQueries, defaultBreakpoint as D)
  );

  const updateBreakpoint = useCallback(
    ({ matches }: MediaQueryList | MediaQueryListEvent, breakpoint: Breakpoint<C>) => {
      if (matches) {
        setCurrentBreakpoint(breakpoint);
      }
    },
    []
  );

  useEnhancedEffect(() => {
    const unsubscribers = mediaQueries.map(({ query, ...breakpoint }) => {
      const mediaQuery = window.matchMedia(query);

      updateBreakpoint(mediaQuery, breakpoint as Breakpoint<C>);

      const handleChange = (event: MediaQueryListEvent) => {
        updateBreakpoint(event, breakpoint as Breakpoint<C>);
      };

      if (mediaQuery?.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
      } else if (mediaQuery?.addListener) {
        mediaQuery.addListener(handleChange);
      }

      return () => {
        if (mediaQuery?.removeEventListener) {
          mediaQuery.removeEventListener('change', handleChange);
        } else if (mediaQuery?.removeListener) {
          mediaQuery.removeListener(handleChange);
        }
      };
    });

    return () => unsubscribers.forEach(unsubscriber => unsubscriber());
  }, [mediaQueries, updateBreakpoint]);

  return currentBreakpoint as UseResponsiveResponse<C, D>;
};

export default useResponsive;
